import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { updateTask } from '../database-tasks';
import { Task } from '@/types/tasks';
import { TaskUpdate, mapDbTaskToUiTask } from '../types';
import { updateGoalCompletionStatus } from '@/hooks/goal-planner/database-goals';
import { useUser } from '@/hooks/auth/use-user';

/**
 * Custom hook for updating a task
 */
export const useUpdateTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  
  return useMutation({
    mutationFn: async ({ 
      taskId, 
      updates, 
      silent = false 
    }: { 
      taskId: string, 
      updates: Partial<Task>, 
      silent?: boolean 
    }) => {
      console.log(`[useUpdateTaskMutation] Starting mutation for task ${taskId}`);
      console.log(`[useUpdateTaskMutation] UI updates received:`, updates);
      
      // Convert UI task updates to DB task updates, but only include fields that were explicitly provided
      const dbUpdates: TaskUpdate = {};
      
      // Only add fields to the update object if they are defined in the updates
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description ?? null;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate ?? null;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority ?? null;
      if (updates.isCompleted !== undefined) dbUpdates.is_completed = updates.isCompleted;
      if (updates.cardOrder !== undefined) dbUpdates.card_order = updates.cardOrder;
      if (updates.goalId !== undefined) dbUpdates.goal_id = updates.goalId ?? null;
      
      // NEW: Handle generated content fields
      if (updates.generated_document_id !== undefined) dbUpdates.generated_document_id = updates.generated_document_id;
      if (updates.generated_image_id !== undefined) dbUpdates.generated_image_id = updates.generated_image_id;
      if (updates.generated_research_id !== undefined) dbUpdates.generated_research_id = updates.generated_research_id;

      console.log(`[useUpdateTaskMutation] DB updates to send:`, dbUpdates);
      console.log(`[useUpdateTaskMutation] About to call updateTask with taskId: ${taskId}`);

      const { data, error } = await updateTask(taskId, dbUpdates);
      
      if (error) {
        console.error(`[useUpdateTaskMutation] Database error:`, error);
        console.error(`[useUpdateTaskMutation] Failed update details:`, {
          taskId,
          dbUpdates,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint,
          errorCode: error.code
        });
        throw new Error(error.message);
      }
      
      console.log(`[useUpdateTaskMutation] ✅ Database update successful for task ${taskId}`);
      console.log(`[useUpdateTaskMutation] Returned data:`, data);
      
      return { 
        task: data ? mapDbTaskToUiTask(data) : null,
        silent,
        updates,
        wasCompletionChanged: updates.isCompleted !== undefined
      };
    },
    onMutate: async ({ taskId, updates }) => {
      console.log(`[useUpdateTaskMutation] onMutate - Optimistic update for task ${taskId}`);
      
      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      await queryClient.cancelQueries({ queryKey: ['assigned-tasks'] });
      await queryClient.cancelQueries({ queryKey: ['task-manager-tasks'] });
      
      // Snapshot all possible query keys this task might be in
      const previousQueries: Record<string, any> = {};
      
      // Helper function to update a task in any query data structure
      const updateTaskInData = (data: any): any => {
        if (!data) return data;
        
        // Handle { active: Task[], completed: Task[] } structure
        if (data.active && data.completed) {
          const updatedActive = data.active.map((task: Task) =>
            task.id === taskId ? { ...task, ...updates } : task
          );
          const updatedCompleted = data.completed.map((task: Task) =>
            task.id === taskId ? { ...task, ...updates } : task
          );
          
          // Handle moving between lists if isCompleted changed
          if (updates.isCompleted !== undefined) {
            const taskInActive = data.active.find((t: Task) => t.id === taskId);
            const taskInCompleted = data.completed.find((t: Task) => t.id === taskId);
            
            if (updates.isCompleted && taskInActive) {
              // Move from active to completed
              return {
                active: data.active.filter((t: Task) => t.id !== taskId),
                completed: [...data.completed, { ...taskInActive, ...updates }]
              };
            } else if (!updates.isCompleted && taskInCompleted) {
              // Move from completed to active
              return {
                active: [...data.active, { ...taskInCompleted, ...updates }],
                completed: data.completed.filter((t: Task) => t.id !== taskId)
              };
            }
          }
          
          return { active: updatedActive, completed: updatedCompleted };
        }
        
        // Handle Task[] structure
        if (Array.isArray(data)) {
          return data.map((task: Task) =>
            task.id === taskId ? { ...task, ...updates } : task
          );
        }
        
        return data;
      };
      
      // Get and update all relevant queries
      const queryKeys = [
        ['tasks'],
        ['tasks', userId],
        ['assigned-tasks'],
        ['assigned-tasks', userId],
        ['task-manager-tasks', userId, 'all'],
        ['task-manager-tasks', userId, 'now']
      ];
      
      queryKeys.forEach(key => {
        const data = queryClient.getQueryData(key);
        if (data) {
          previousQueries[JSON.stringify(key)] = data;
          queryClient.setQueryData(key, updateTaskInData(data));
        }
      });
      
      return { previousQueries };
    },
    onError: (err, variables, context) => {
      console.error(`[useUpdateTaskMutation] onError - Rolling back optimistic update`);
      
      // Rollback all queries to their previous values
      if (context?.previousQueries) {
        Object.entries(context.previousQueries).forEach(([keyStr, data]) => {
          const key = JSON.parse(keyStr);
          queryClient.setQueryData(key, data);
        });
      }
      
      toast({
        title: "Error",
        description: `Failed to update task: ${err.message}`,
        variant: "destructive"
      });
    },
    onSuccess: async ({ task, silent, updates, wasCompletionChanged }) => {
      // Invalidate affected queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['assigned-tasks'] });
      
      // If completion status changed and task has a goal, update goal completion
      if (wasCompletionChanged && task?.goalId) {
        try {
          await updateGoalCompletionStatus(task.goalId, user.id);
          queryClient.invalidateQueries({ queryKey: ['goals'] });
        } catch (error) {
          console.error('Failed to update goal completion status:', error);
        }
      }
      
      if (!silent) {
        toast({
          title: "Task updated",
          description: "Task has been updated successfully"
        });
      }
    }
  });
};
